import React, { useState } from "react";
import { Button, Input, Modal, FileInput, DatePicker, Toast, TeamSearch, ButtonGroup, Checkbox, ErrorMsg, Chips, Select } from "../../";
import { apis, post } from "../../../Utils/axios";
import moment from "moment";
import { useAdminPermission } from "../../../Hooks/useAdminPermission";
import { getNameValidationError, validateEmail, validatePhoneNumber } from "Utils/utils";
import { formatDobInput, parseDobInput } from "../../../Utils/dob";
import { COUNTRIES } from "Utils/constants";
import AddMultipleUsers from "./addMultipleUsers";

function AddEmployee({ teamId, onReload = () => {}, btnLabel = "+ Add User", btnClass = "", btnVariant = "primary" }) {
	const { checkAdminPermission } = useAdminPermission();
	const [NewEmployee, setNewEmployee] = useState();
	const [SendInvite, setSendInvite] = useState(true);
	const [Emails, setEmails] = useState([]);
	const [showResultModal, setShowResultModal] = useState(false);
	const [resultMode, setResultMode] = useState("invite");
	const [resultData, setResultData] = useState({ total: 0, success: 0, failed: 0 });
	const [resultWarning, setResultWarning] = useState("");

	const initial = {
		fName: "",
		lName: "",
		email: "",
		gender: {
			label: "Male",
			value: "MALE",
		},
		phone: "",
		photo: "",
		team: {},
		country: {},
		// TWCP-191: do not prefill DOB; admin must set explicitly
		dob: null,
	};
	const [Values, setValues] = useState(initial);
	const [DobInput, setDobInput] = useState("");

	const [Loader, setLoader] = useState();
	const [Error, setError] = useState();

	const parseEmailList = (raw = "") => {
		return `${raw}`
			.split(/[;,]/)
			.map((e) => e.trim())
			.filter(Boolean);
	};

	const onSubmit = async () => {
		if (!teamId && !Values.team.value) {
			return setError("Team is required for adding user");
		}
		const enteredEmails = parseEmailList(Values.email);
		if (enteredEmails.length !== 1) {
			return setError("Please enter only one email address when creating a user");
		}
		if (!validateEmail(enteredEmails[0])) {
			return setError("Please enter valid email address");
		}
		const nameError = getNameValidationError("First name", Values.fName) || getNameValidationError("Last name", Values.lName);
		if (nameError) {
			return setError(nameError);
		}
		if (!Values.gender) {
			return setError("Please select gender");
		}
		if (!Values.email) {
			return setError("Please enter email address");
		}
		if (!Values.dob) {
			return setError("Please enter date of birth");
		}
		setError();
		setLoader(true);
		if (!(await validatePhoneNumber(Values.phone, Values.country.value))) {
			setLoader(false);
			return Toast.error("Please enter valid phone number");
		}

		post(
			apis.addUser,
			{
				...Values,
				// Backend expects DOB as YYYY-MM-DD (date only)
				dob: moment(Values.dob).format("YYYY-MM-DD"),
				phoneCode: Values.country?.value,
				gender: Values.gender?.value,
				teamId: teamId || Values.team.value,
				photo: Values.photo?.split("?")[0],
			},
			(r, e) => {
				if (r) {
					Toast.success("Verification link sent to user");
					setNewEmployee(false);
					setLoader(false);
					onReload();
					setValues(initial);
				} else if (e) {
					if (e.response.status === 409) {
						setError("User already exists, please use different email address");
					} else {
						Toast.handleError(e);
					}
					setLoader(false);
				}
			}
		);
	};

	const sendInvite = () => {
		let emails = [...Emails, ...parseEmailList(Values.email)];
		emails = Array.from(new Set(emails));
		if (!emails.length) {
			return setError("Please enter at least one email address");
		}
		const invalid = emails.find((e) => !validateEmail(e));
		if (invalid) {
			return setError(`Please enter valid email address: ${invalid}`);
		}
		setError();
		setLoader(true);
		post(apis.sendInvite, { emails }, (r, e) => {
			if (r) {
				Toast.success("Invite sent !!");
				setNewEmployee(false);
				setLoader(false);
				onReload();
					setValues(initial);
					setEmails([]);
			} else if (e) {
				if (e.response.status === 409) {
					setError("User already exists, please use different email address");
				} else {
					Toast.handleError(e);
				}
				setLoader(false);
			}
		});
	};

	if (!checkAdminPermission("EDIT_USERS")) {
		return null;
	}

	const onResult = (res) => {
		if (!res) {
			return;
		}
		const { totalRows, successRows, failedRows, warning, reportMailSent } = res;
		setResultMode(SendInvite ? "invite" : "create");
		setResultData({
			total: totalRows,
			success: successRows,
			failed: failedRows,
		});
		setResultWarning(reportMailSent === false ? (warning || "Summary report email could not be sent.") : "");

		setNewEmployee(false);
		setShowResultModal(true);
	};

	return (
		<>
			<Button onClick={() => setNewEmployee(true)} variant={btnVariant} className={btnClass}>
				{btnLabel}
			</Button>
			<Modal
				open={NewEmployee}
				onClose={() => {
					setError();
					setNewEmployee();
					setValues(initial);
					setSendInvite(true);
					setEmails([]);
				}}
			>
				<div className="p-6">
					<div className="flex justify-evenly mb-4">
						<button className={`px-4 py-2 ${SendInvite ? "border-b-2 border-primary font-semibold" : "text-gray-500"}`} onClick={() => setSendInvite(true)}>
							Invite User
						</button>
						<button className={`px-4 py-2 ${!SendInvite ? "border-b-2 border-primary font-semibold" : "text-gray-500"}`} onClick={() => setSendInvite(false)}>
							Create User
						</button>
					</div>
					<div className="my-2 text-label">
						{SendInvite
							? "Send an invite by entering one email address, or invite multiple users at once by separating email addresses with commas or semicolons."
							: "Create one user by entering a single email address and completing the details below."}
					</div>
					<div className="flex flex-col mt-4 gap-y-4">
						{SendInvite && <div className="text-xs text-secondary">Example: `alex@company.com, sam@company.com`</div>}
						<Input
							type="text"
							allowSpecialChars={true}
							required
							value={Values.email}
							onChange={(email) => setValues((prev) => ({ ...prev, email }))}
							label="Email Address"
							placeholder={SendInvite ? "Enter one or more email addresses separated by commas or semicolons" : "Enter email address"}
							title={SendInvite ? "Enter one or more email addresses separated by commas or semicolons" : "Enter email address"}
							className="w-full"
						/>
						{SendInvite && Emails.length > 0 && (
							<Chips className="flex-wrap gap-2" options={Emails.map((email) => ({ label: email, value: email }))} onDelete={(email) => setEmails((prev) => prev.filter((e) => e !== email.value))} />
						)}
						{SendInvite && (
							<>
								<div className="text text-label">Or you can upload a file containg users email to invite multiple user at a time.
									<span className="font-bold">( Only .xlx, .xlsx or .csv format allowed, Max size up to 500KB. )</span>
								</div>
								<AddMultipleUsers SendInvite={true} onResult={onResult} />									
							</>
						)}
						{!SendInvite && (
							<>
								{!teamId && <TeamSearch label="Team this user belongs to" placeholder="Search Teams" onSelect={(team) => setValues((prev) => ({ ...prev, team }))} selected={Values.team} />}
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<Input noNumbers required value={Values.fName} onChange={(fName) => setValues((prev) => ({ ...prev, fName }))} label="First Name" placeholder="First Name" />
									<Input noNumbers required value={Values.lName} onChange={(lName) => setValues((prev) => ({ ...prev, lName }))} label="Last Name" placeholder="Last Name" />
								</div>
								<Input
									value={DobInput}
									onChange={(value) => {
										const formattedDob = formatDobInput(value);
										const parsedDob = parseDobInput(formattedDob);
										setDobInput(formattedDob);
										if (parsedDob.error) {
											setError(parsedDob.error);
											setValues((prev) => ({ ...prev, dob: null }));
										} else {
											setError();
											setValues((prev) => ({ ...prev, dob: parsedDob.value }));
										}
									}}
									required
									label="Date of Birth"
									placeholder="DD/MM/YYYY"
									maxLength={10}
									allowSpecialChars
									inputClass="tracking-wide"
								/>
								<label>Phone Number</label>
								<div className="flex flex-col gap-2 sm:flex-row">
									<Select className="w-full sm:w-auto sm:max-w-min" placeholder="Code" selected={Values.country} onChange={(country) => setValues((prev) => ({ ...prev, country }))} required label="" options={COUNTRIES} />
									<Input className="flex-1" type="number" required value={Values.phone} onChange={(phone) => setValues((prev) => ({ ...prev, phone }))} placeholder="Phone" />
								</div>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<FileInput file={Values.photo} onUpload={(obj) => setValues((prev) => ({ ...prev, photo: obj.file.key }))} label="Profile Photo" />
									<div>
										<label className="block mb-2">Gender</label>
										<ButtonGroup
											selected={Values.gender}
											onClick={(gender) => setValues((prev) => ({ ...prev, gender }))}
											options={[
												{
													label: "Male",
													value: "MALE",
												},
												{
													label: "Female",
													value: "FEMALE",
												},
											]}
										/>
									</div>
								</div>
							</>
						)}
						<ErrorMsg e={Error} />
						{SendInvite ? (
							<Button loading={Loader} onClick={sendInvite} className="w-full mt-4">
								Send Invite
							</Button>
						) : (
							<Button loading={Loader} onClick={onSubmit} className="w-full mt-4">
								Create Employee
							</Button>
						)}
					</div>
				</div>
			</Modal>

			<Modal open={showResultModal} onClose={() => {
				setShowResultModal(false);
				setResultWarning("");
			}}>
				<div className="p-6 text-center">
					<h2 className="text-xl font-semibold mb-4"> {resultMode === "invite" ? "Invitation Summary" : "User Creation Summary"} </h2>

					<div className="grid grid-cols-3 gap-4 mb-6">
					<div className="bg-gray-100 p-3 rounded">
						<p className="text-sm text-gray-500">{resultMode === "invite" ? "Total Invitation Mails" : "Total Rows"}</p>
						<p className="text-lg font-bold">{resultData.total}</p>
					</div>

					<div className="bg-green-100 p-3 rounded">
						<p className="text-sm text-gray-500">{resultMode === "invite" ? "Success Invitations" : "Users Created"}</p>
						<p className="text-lg font-bold text-green-600">{resultData.success}</p>
					</div>

					<div className="bg-red-100 p-3 rounded">
						<p className="text-sm text-gray-500">{resultMode === "invite" ? "Failed To Invite" : "Failed To Create"}</p>
						<p className="text-lg font-bold text-red-600">{resultData.failed}</p>
					</div> 
					</div>

					{resultWarning ? (
						<div className="mb-4 rounded bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
							{resultWarning}
						</div>
					) : (
						<p className="text-sm text-gray-600 mb-4">
							Detailed report has been sent to your email.
						</p>
					)}

					<Button onClick={() => {
						setShowResultModal(false);
						setResultWarning("");
					}} className="w-full"> Close </Button>
				</div>
			</Modal>
		</>
	);
}

export default AddEmployee;
